import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import { useProductGroupsExtended } from '../../../lib/query/hooks';
import { downloadCSV } from '../../../lib/utils';
import type { SimpleAccount } from '@/lib/types';

interface ProductGroupTreeListProps {
  account: SimpleAccount;
}

export function ProductGroupTreeList({ account }: ProductGroupTreeListProps) {
  const {
    data: productGroupTrees = [],
    isLoading: loading,
    error,
  } = useProductGroupsExtended(account.code);

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    const csvData = productGroupTrees.map((tree) => ({
      タイプカスタマーID: tree.product_group_type_customer_id || '',
      タイプ名: tree.product_group_type_name || '',
      '1階層カスタマーID': tree.product_group_customer_id1 || '',
      '1階層名': tree.product_group_name1 || '',
      '2階層カスタマーID': tree.product_group_customer_id2 || '',
      '2階層名': tree.product_group_name2 || '',
      '3階層カスタマーID': tree.product_group_customer_id3 || '',
      '3階層名': tree.product_group_name3 || '',
      '4階層カスタマーID': tree.product_group_customer_id4 || '',
      '4階層名': tree.product_group_name4 || '',
      '5階層カスタマーID': tree.product_group_customer_id5 || '',
      '5階層名': tree.product_group_name5 || '',
    }));

    const filename = `商品階層一覧_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>商品階層一覧を読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error instanceof Error ? error.message : '商品階層一覧の読み込みに失敗しました。'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">
              商品階層一覧（拡張版） ({productGroupTrees.length}件)
            </Typography>
            {productGroupTrees.length > 0 && (
              <Button variant="outlined" onClick={handleDownloadCSV} size="small">
                CSVでダウンロード
              </Button>
            )}
          </Box>

          {productGroupTrees.length === 0 ? (
            <Alert severity="info">商品階層データが見つかりませんでした。</Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTable-root': {
                  minWidth: 1200,
                },
              }}
            >
              <Table size="small" sx={{ whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>タイプカスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>タイプ名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>1階層カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>1階層名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>2階層カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>2階層名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>3階層カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>3階層名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>4階層カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>4階層名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>5階層カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>5階層名</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productGroupTrees.map((productGroupTree, index) => (
                    <TableRow key={`${productGroupTree.id}-${index}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_type_customer_id || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_type_name || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_customer_id1 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_name1 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_customer_id2 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_name2 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_customer_id3 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_name3 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_customer_id4 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_name4 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_customer_id5 || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {productGroupTree.product_group_name5 || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
